import moment, { Moment } from 'moment-timezone';
import { DateStr, DateTimeStr, TimeRange, Timestamp, TimeStr } from '../types/date';

type DateType = Moment;

export function getDurationForRange(
  from: number | DateTimeStr,
  to: number | DateTimeStr,
  options = {},
) {
  const mFrom = moment(from);
  const mTo = moment(to);

  if (mTo < mFrom) {
    return '-';
  } else {
    mTo.add(1, 'day');
  }

  return formatDuration(mTo.diff(mFrom), options);
}

export function formatDuration(ms: number, options: { verbose?: boolean } = {}) {
  const duration = moment.duration(ms);
  const [h, m, s] = [duration.hours(), duration.minutes(), duration.seconds()];

  const suffixes = options.verbose
    ? {
        h: (v: number) => (v === 1 ? ' hour' : ' hours'),
        m: (v: number) => (v === 1 ? ' minute' : ' minutes'),
        s: (v: number) => (v === 1 ? ' second' : ' seconds'),
      }
    : {
        h: () => 'h',
        m: () => 'm',
        s: () => 's',
      };

  if (h === 0 && m === 0) {
    return `${s}${suffixes.s(s)}`;
  } else if (h === 0 && s === 0) {
    return `${m}${suffixes.m(m)}`;
  } else if (h === 0) {
    return `${m}${suffixes.m(m)} ${s}${suffixes.s(s)}`;
  }

  return `${h}${suffixes.h(h)} ${m}${suffixes.m(m)} ${s}${suffixes.s(s)}`;
}

export function roundToSeconds(timestamp: number) {
  const mDate = moment(timestamp);

  mDate.subtract(mDate.seconds() % 30, 'seconds');
  mDate.millisecond(0);

  return mDate.valueOf();
}

export function parseRange({ from, to, length }: any) {
  const fromTs = from && dateToTimestamp(from);
  const toTs = to && dateToTimestamp(to);

  if (fromTs && toTs) {
    return {
      from: fromTs,
      to: toTs,
    };
  }

  let lengthMs;
  if (length) {
    if (typeof length === 'number' || /^\d+$/.test(length)) {
      lengthMs = Number(length) * 1000;
    } else {
      const [, lenMin, lenSec] = (length || '').toLowerCase().match(/^(?:(\d+)m)?(?:(\d+)s)?$/);

      if (lenMin || lenSec) {
        lengthMs = ((Number(lenMin) || 0) * 60 + (Number(lenSec) || 0)) * 1000;
      }
    }
  }

  if (fromTs && lengthMs) {
    return {
      from: fromTs,
      to: fromTs + lengthMs,
    };
  }

  if (toTs && lengthMs) {
    return {
      from: toTs - lengthMs,
      to: toTs,
    };
  }

  return null;
}

export function dateToTimestamp(date: any) {
  if (!date) {
    return null;
  }

  if (typeof date === 'number') {
    return date;
  }

  // Si from/to es un string de dígitos, convertirlos a Number
  if (/^\d+$/.test(date)) {
    date = Number(new Date(Number(date)));
  } else {
    date = Number(new Date(date));
  }

  return isNaN(date) ? null : date;
}

export function minsToMs(mins: number) {
  return mins * 60 * 1000;
}

export function hoursToMs(hours: number) {
  return hours * 60 * 60 * 1000;
}

export function minutesToMs(minutes: number) {
  return minutes * 60 * 1000;
}

export function rangeChanged<T extends number | DateTimeStr>(
  oldRange: TimeRange<T>,
  newRange: TimeRange<T>,
): boolean {
  return oldRange.from !== newRange.from || oldRange.to !== newRange.to;
}

export function toDateRange(date: DateStr, tz?: string): TimeRange<DateTimeStr> {
  let mFrom = moment(date).startOf('day');
  let mTo = moment(date).endOf('day');

  if (tz) {
    mFrom = mFrom.tz(tz, true).startOf('day');
    mTo = mTo.tz(tz, true).endOf('day');
  }

  return {
    from: toDateTimeStr(mFrom, !!tz && tz !== 'UTC'),
    to: toDateTimeStr(mTo, !!tz && tz !== 'UTC'),
  };
}

export function toDateDiffRange(
  dateFrom: DateStr,
  dateTo: DateStr,
  tz?: string,
): TimeRange<DateTimeStr> {
  let mFrom = moment(dateFrom).startOf('day');
  let mTo = moment(dateTo).endOf('day');

  if (tz) {
    mFrom = mFrom.tz(tz, true).startOf('day');
    mTo = mTo.tz(tz, true).endOf('day');
  }

  return {
    from: toDateTimeStr(mFrom, !!tz && tz !== 'UTC'),
    to: toDateTimeStr(mTo, !!tz && tz !== 'UTC'),
  };
}

export function toDateRangeCustom(
  date: DateStr,
  startHour: string,
  endHour: string,
): TimeRange<DateTimeStr> {
  const start = startHour.split(':').map(Number);
  const end = endHour.split(':').map(Number);

  return {
    from: toDateTimeStr(moment(date).startOf('day').hour(start[0]).minute(start[1])),
    to: toDateTimeStr(moment(date).endOf('day').hour(end[0]).minute(end[1])),
  };
}

export function toTimestamp(d: DateTimeStr | string | number | Date): Timestamp {
  return Number(new Date(d)) as Timestamp;
}

export function formatDate(
  d: DateTimeStr | Date | DateStr | number | string,
  format: string,
  tz?: string,
): string {
  return (tz ? moment(d).tz(tz) : moment(d)).format(format);
}

export function toDateTimeStr(
  date: string | number | Date | Moment,
  keepOffset?: boolean,
): DateTimeStr {
  const isStringTimestamp = typeof date === 'string' && /^\d{13}$/.test(date);

  if (typeof date === 'string' && !isStringTimestamp) {
    if (checkValidDateTimeStr(date)) {
      return date;
    } else {
      throw new Error(`Invalid date time string: ${date}. It must be a valid ISO8601 value`);
    }
  } else {
    const dateString = moment(isStringTimestamp ? Number(date) : date).toISOString(keepOffset);

    if (checkValidDateTimeStr(dateString)) {
      return dateString;
    }
  }

  throw new Error(`Invalid date ${date} provided`);
}

function checkValidDateTimeStr(str: string): str is DateTimeStr {
  return (
    !!str &&
    str.match(
      /^(-?(?:[1-9][0-9]*)?[0-9]{4})-(1[0-2]|0[1-9])-(3[01]|0[1-9]|[12][0-9])T(2[0-3]|[01][0-9]):([0-5][0-9]):([0-5][0-9])(\\.[0-9]+)?(\.\d\d\d)?(([+-]\d\d:\d\d)|Z)?$/,
    ) !== null
  );
}

export function toDateStr(d: string | number | Date | Moment): DateStr {
  return moment(d).format('YYYY-MM-DD') as DateStr;
}

export function toTimeStr(d: string | number | Date | Moment): TimeStr {
  return moment(d).format('hh:mm:ss') as TimeStr;
}

export function date(): DateType {
  return moment();
}

export function now(): DateTimeStr {
  return moment().toISOString() as DateTimeStr;
}

export function today(): DateStr {
  return moment().format('YYYY-MM-DD') as DateStr;
}

export function yesterday() {
  return moment().subtract(1, 'days').format('YYYY-MM-DD') as DateStr;
}

export function nowTs(): Timestamp {
  return Date.now() as Timestamp;
}

export function rangeDuration(from: DateTimeStr | string, to: DateTimeStr | string): number {
  return toTimestamp(to) - toTimestamp(from);
}

export function parseRangeFromTs(range: TimeRange<number>): TimeRange<DateTimeStr> {
  return {
    from: toDateTimeStr(range.from),
    to: toDateTimeStr(range.to),
  };
}

export function rangesOverlap<T extends number | DateTimeStr>(
  range1: TimeRange<T>,
  range2: TimeRange<T>,
) {
  return range1.from <= range2.to && range1.to >= range2.from;
}

export function startOfDay(value: Moment): Moment {
  return value.clone().hours(0).minutes(0).seconds(0).milliseconds(0);
}

export function endOfDay(value: Moment): Moment {
  return value.clone().hours(23).minutes(59).seconds(59).milliseconds(999);
}

export function getBrowserTimezone(): string {
  return moment.tz.guess(true);
}

export function forceTz(value: DateTimeStr, targetTz?: string): DateTimeStr {
  if (!targetTz) {
    return value;
  }

  const targetOffset = moment(value).tz(targetTz).utcOffset();

  return moment(value).utcOffset(targetOffset, true).toISOString(true) as DateTimeStr;
}

/**
 * Builds a moment in the given timezone with the calendar date from `date`
 * and the time of day (hour, minute, second, millisecond) from `referenceTs`.
 * Use when the user changes only the date and the time should stay the same.
 */
export function setDateKeepingTime(referenceTs: number, date: Date, tz: string): Moment {
  const currentMoment = moment(referenceTs).tz(tz);
  return moment(date)
    .tz(tz)
    .hour(currentMoment.hour())
    .minute(currentMoment.minute())
    .second(currentMoment.second())
    .millisecond(currentMoment.millisecond());
}

/**
 * Builds a moment in the given timezone with the same calendar day as `referenceTs`
 * and the time of day from `time` (hours and minutes; seconds and milliseconds set to 0).
 * Use when the user changes only the time and the date should stay the same.
 */
export function setTimeKeepingDate(referenceTs: number, time: Date, tz: string): Moment {
  const currentDayInTz = moment(referenceTs).tz(tz);

  return currentDayInTz
    .clone()
    .hour(time.getHours())
    .minute(time.getMinutes())
    .second(0)
    .millisecond(0);
}
